import { AUTH_ROLES } from "@/constants/auth";

const normalizePathname = (pathname) =>
  String(pathname ?? "")
    .trim()
    .replace(/\/+$/, "") || "/";

const isPath = (pathname, routePath) => {
  const path = normalizePathname(pathname);
  const target = normalizePathname(routePath);
  return path === target || path.startsWith(`${target}/`);
};

export const canAccessProperties = (a) =>
  Boolean(a.isSuperAdmin || a.isCampaignUser || a.isRealtor || a.isHomeowner || a.isWholesaler || a.isContractor);
export const canAccessCampaigns = (a) => Boolean(a.isSuperAdmin || a.isCampaignUser || a.isRealtor);
export const canAccessDeals = (a) => a.isSuperAdmin || a.isCampaignUser || a.isInvestor || a.isRealtor;
export const canAccessDealLensSharedLink = (a) => Boolean(a.isSuperAdmin || a.isInvestor);
export const canAccessOpportunities = (a) => a.isSuperAdmin || a.isInvestor;

/**
 * Route gate rules used by focus reconciliation and loader metadata.
 * `requiredRole` is the role associated with that route family for mismatch recovery.
 */
export const ROLE_GATED_ROUTE_RULES = [
  {
    id: "properties",
    requiredRole: AUTH_ROLES.REALTOR,
    allow: canAccessProperties,
    matches: (pathname) => isPath(pathname, "/properties") || isPath(pathname, "/property"),
  },
  {
    id: "campaigns",
    requiredRole: AUTH_ROLES.REALTOR,
    allow: canAccessCampaigns,
    matches: (pathname) =>
      isPath(pathname, "/campaigns") ||
      isPath(pathname, "/campaign-builder") ||
      isPath(pathname, "/real-estate-agent") ||
      isPath(pathname, "/conversation") ||
      isPath(pathname, "/unified-email-inbox"),
  },
  {
    id: "opportunities",
    requiredRole: AUTH_ROLES.INVESTOR,
    allow: canAccessOpportunities,
    matches: (pathname) => isPath(pathname, "/opportunities"),
  },
  {
    id: "deallens-shared-link",
    requiredRole: AUTH_ROLES.INVESTOR,
    allow: canAccessDealLensSharedLink,
    matches: (pathname) =>
      /^\/deallens\/[^/]+\/[^/]+\/[^/]+\/?$/.test(
        String(pathname ?? "")
          .trim()
          .replace(/\/+$/, "")
      ),
  },
  {
    id: "deallens",
    requiredRole: AUTH_ROLES.INVESTOR,
    allow: canAccessDeals,
    matches: (pathname) => isPath(pathname, "/deallens"),
  },
];

export function resolveRoleGateForPath(pathname) {
  return ROLE_GATED_ROUTE_RULES.find((rule) => rule.matches(pathname)) ?? null;
}

export function canAccessPathWithCurrentRole(pathname, authStore) {
  const rule = resolveRoleGateForPath(pathname);
  if (!rule) return true;
  return Boolean(rule.allow(authStore));
}

function roleFlagsFor(role) {
  const normalized = String(role ?? "")
    .trim()
    .toLowerCase();
  return {
    isSuperAdmin: normalized === AUTH_ROLES.SUPERADMIN,
    isCampaignUser: normalized === AUTH_ROLES.CAMPAIGN,
    isRealtor: normalized === AUTH_ROLES.REALTOR,
    isHomeowner: normalized === AUTH_ROLES.HOMEOWNER,
    isInvestor: normalized === AUTH_ROLES.INVESTOR,
    isContractor: normalized === AUTH_ROLES.CONTRACTOR,
    isWholesaler: normalized === AUTH_ROLES.WHOLESALER,
  };
}

export function canAccessPathWithRole(pathname, role, authStore) {
  const rule = resolveRoleGateForPath(pathname);
  if (!rule) return true;
  const roleFlags = roleFlagsFor(role);
  return Boolean(
    rule.allow({
      ...(authStore ?? {}),
      ...roleFlags,
    })
  );
}
